export type HubCoursePrice = {
  url: string;
  contentfulId: string | null;
  title: string | null;
  /** Formatted inc-VAT display like "£1,500" from CRM, or null if unavailable */
  priceIncVatLabel: string | null;
  /** Ex-VAT pence from CRM standard/seat price, or null */
  priceExVatPence: number | null;
  event: any | null;
  /**
   * Absolute URL for the course listing/OG image from CMS, or null when missing.
   * Prefer listingImage, then metadata image (same sources as course listing cards).
   */
  imageUrl: string | null;
};

/** Prefix VTCT once for hub course card titles and image alt text. */
export function ensureVtctCourseName(name: string): string {
  const trimmed = name.trim();
  if (!trimmed) {
    return trimmed;
  }
  if (/^vtct\b/i.test(trimmed)) {
    return trimmed.replace(/^vtct\b/i, "VTCT");
  }
  return `VTCT ${trimmed}`;
}

/** Resolve a Contentful asset URL string to an absolute https URL. */
export function resolveContentfulFileUrl(value: unknown): string | null {
  if (typeof value !== "string") {
    return null;
  }
  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }
  if (trimmed.startsWith("http://") || trimmed.startsWith("https://")) {
    return trimmed;
  }
  if (trimmed.startsWith("//")) {
    return `https:${trimmed}`;
  }
  if (trimmed.startsWith("/")) {
    return trimmed;
  }
  return null;
}

/** Same image sources listing cards / OG use: listingImage, then metadata.image. */
export function resolveHubCourseImageUrl(course: {
  fields?: {
    listingImage?: { fields?: { file?: { url?: unknown } } };
    metadata?: { fields?: { image?: { fields?: { file?: { url?: unknown } } } } };
  };
} | null | undefined): string | null {
  const listing = resolveContentfulFileUrl(
    course?.fields?.listingImage?.fields?.file?.url,
  );
  if (listing) {
    return listing;
  }
  return resolveContentfulFileUrl(
    course?.fields?.metadata?.fields?.image?.fields?.file?.url,
  );
}

/** Returns the CRM label, or a neutral phrase (never invents a £ figure). */
export function formatPriceOrFallback(
  label: string | null | undefined,
): string {
  return label || "the current course fee";
}

function parseGbpLabelToPence(label: string): number | null {
  const cleaned = label
    .replace(/£/g, "")
    .replace(/,/g, "")
    .replace(/\s/g, "")
    .trim();
  if (!cleaned) return null;
  const pounds = Number.parseFloat(cleaned);
  if (!Number.isFinite(pounds)) return null;
  return Math.round(pounds * 100);
}

/** Parse £X,XXX labels, sum, and reformat. Returns null if any label is missing. */
export function sumIncVatLabels(
  labels: Array<string | null | undefined>,
): string | null {
  if (!labels.length) return null;

  let totalPence = 0;
  for (const label of labels) {
    if (!label) return null;
    const pence = parseGbpLabelToPence(label);
    if (pence == null) return null;
    totalPence += pence;
  }

  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
  }).format(totalPence / 100);
}
